import { useCallback, useEffect, useRef, useState } from 'react'
import BackRow from './BackButton'
import type { User } from '../lib/auth'
import Icon, { type IconName } from '../components/Icon'
import { worx, type Membership } from './client'
import { tw } from './strings'
import Profile from './Profile'
import Applications from './Applications'
import { NATIVE_HOME } from './nativeNavigation'
import Dashboard from './Dashboard'
import BusinessScreen from './BusinessScreen'
import Mailbox from './Mailbox'
import { Detail, Feed, Saved } from './Jobs'
import { Applicants, Postings, Register } from './Business'
import AccountPanel from './AccountPanel'
import { COUNTS, GOTO, TAB, emit, type CountsDetail, type GotoDetail, type TabDetail } from './desktop'
import { MyRequests, MyServices, SavedServices, ServiceDetailView, ServicesFeed } from './Services'
import { sv } from './servicesStrings'

type Side = 'worker' | 'employer' | 'pro'
type WorkerTab = 'jobs' | 'saved' | 'applications' | 'messages' | 'profile'
type EmployerTab = 'home' | 'postings' | 'applicants' | 'messages' | 'business'
type ProTab = 'find' | 'saved' | 'requests' | 'ads' | 'profile'

/**
 * The signed-in application.
 *
 * ── Why the two sides are a toggle and not two apps ─────────────────────────
 *
 * The plan is explicit that the opening choice does not lock the account, and
 * this is where that promise is either kept or quietly broken. One person can
 * hold a worker profile and an employer membership, so the switch is always
 * present for anyone who has both and absent for everyone else — an account
 * with no business does not need a control that offers to show them one.
 *
 * The side is remembered in localStorage rather than on the account. It is a
 * preference about this device, not a fact about the person: the manager who
 * checks applicants on the office tablet and looks for weekend work on their
 * phone is one account and two sensible defaults.
 */

/**
 * One tab. Icon over label, with the unread count riding on the icon.
 *
 * A component rather than two hand-written bars: the worker has five tabs and
 * the employer three, and the first time they were written separately the
 * badge ended up on one and not the other.
 */
function Tab({
  icon, label, current, badge, onClick, main = false,
}: {
  icon: IconName
  label: string
  current: boolean
  badge?: number
  onClick: () => void
  /** The big raised one in the middle of the bar — the thing the app is for. */
  main?: boolean
}) {
  return (
    <button aria-current={current} onClick={onClick} className={main ? 'w-tab-main' : undefined}>
      <span className="w-tab-icon">
        <Icon name={icon} size={main ? 30 : 22} />
        {Boolean(badge) && (
          // The count is in the badge AND in the accessible label: a coloured
          // dot is not information to a screen reader.
          <span className="w-badge" aria-hidden="true">{badge! > 9 ? '9+' : badge}</span>
        )}
      </span>
      <span className="w-tab-label">{label}</span>
      {Boolean(badge) && <span className="w-sr">{badge} {tw('w.unread')}</span>}
    </button>
  )
}

export default function Worx({
  user,
  side,
  onSignedOut,
  onSwitchToWorker,
  environment,
}: {
  user: User
  /** Owned by App now, so the switch survives leaving this screen. */
  side: Side
  onSignedOut: () => void | Promise<void>
  /** App owns which side is showing; the Business screen offers to change it. */
  onSwitchToWorker: () => void
  environment?: string
}) {
  const [memberships, setMemberships] = useState<Membership[] | null>(null)
  const [workerTab, setWorkerTab] = useState<WorkerTab>('jobs')
  const [employerTab, setEmployerTab] = useState<EmployerTab>('home')
  const [proTab, setProTab] = useState<ProTab>('find')
  const [openService, setOpenService] = useState<string | null>(null)
  const [newService, setNewService] = useState(false)
  const [newRequests, setNewRequests] = useState(0)
  const [openJob, setOpenJob] = useState<string | null>(null)
  const [openApplicants, setOpenApplicants] = useState<string | null>(null)
  const [openThread, setOpenThread] = useState<string | null>(null)
  const [activeEmployer, setActiveEmployer] = useState<string | null>(null)
  // Set by the dashboard's Post a Job button; consumed by Postings, which
  // owns the editor.
  const [newPost, setNewPost] = useState(false)
  const [unread, setUnread] = useState(0)
  const [profileVisited, setProfileVisited] = useState(false)
  const [profileRequest, setProfileRequest] = useState(0)
  const [profileRevision, setProfileRevision] = useState(0)
  const [profileReturn, setProfileReturn] = useState<WorkerTab | null>(null)
  const [businessReturn, setBusinessReturn] = useState<EmployerTab | null>(null)
  const workerTabRef = useRef(workerTab)
  const profileReturnRef = useRef(profileReturn)
  useEffect(() => {
    const home = () => {
      setWorkerTab('jobs'); setEmployerTab('home'); setProTab('find'); setOpenService(null)
      setOpenJob(null); setOpenApplicants(null); setOpenThread(null)
      setProfileReturn(null); setBusinessReturn(null)
    }
    window.addEventListener(NATIVE_HOME, home)
    return () => window.removeEventListener(NATIVE_HOME, home)
  }, [])
  useEffect(() => { workerTabRef.current = workerTab; profileReturnRef.current = profileReturn }, [workerTab, profileReturn])

  function completeProfile() {
    setProfileReturn(workerTab === 'profile' ? 'jobs' : workerTab)
    setProfileVisited(true)
    setProfileRequest((n) => n + 1)
    setWorkerTab('profile')
  }
  function returnFromProfile() {
    setWorkerTab(profileReturn ?? 'jobs')
    setProfileReturn(null)
  }
  function reviewBusiness() {
    setBusinessReturn(employerTab)
    setEmployerTab('business')
  }


  const loadMemberships = useCallback(async () => {
    try {
      setMemberships((await worx.myEmployers()).employers)
    } catch {
      setMemberships([])
    }
  }, [])

  useEffect(() => { void loadMemberships() }, [loadMemberships])

  // The unread count on the tab. Loaded once per mount rather than polled: a
  // push notification is what tells someone a message arrived, and a timer
  // here would be a second, worse version of that.
  useEffect(() => {
    void worx.threads().then((r) => setUnread(r.unread)).catch(() => {})
  }, [workerTab, employerTab])

  // New service requests waiting on this person, for the badge on My Services.
  // Same rule as the mailbox count: read on tab change, not polled.
  const refreshRequestCount = useCallback(() => {
    void worx.myServices()
      .then((r) => setNewRequests(r.services.reduce((n, s) => n + (s.newRequests ?? 0), 0)))
      .catch(() => {})
  }, [])
  useEffect(() => { if (side === 'pro') refreshRequestCount() }, [side, proTab, refreshRequestCount])

  // The desktop top bar (DesktopNav) shows which tab is up and what is waiting, and asks for tabs.
  const currentTab = side === 'worker' ? workerTab : side === 'employer' ? employerTab : proTab
  useEffect(() => { emit<TabDetail>(TAB, { side, tab: currentTab }) }, [side, currentTab])
  useEffect(() => { emit<CountsDetail>(COUNTS, { unread, requests: newRequests }) }, [unread, newRequests])
  useEffect(() => {
    const WORKER: WorkerTab[] = ['jobs', 'saved', 'applications', 'messages', 'profile']
    const EMPLOYER: EmployerTab[] = ['home', 'postings', 'applicants', 'messages', 'business']
    const PRO: ProTab[] = ['find', 'saved', 'requests', 'ads', 'profile']
    const on = (e: Event) => {
      const tab = (e as CustomEvent<GotoDetail>).detail.tab
      if (side === 'worker' && (WORKER as string[]).includes(tab)) {
        if (tab === 'profile') setProfileVisited(true)
        if (tab === 'jobs') setOpenJob(null)
        setWorkerTab(tab as WorkerTab)
      } else if (side === 'employer' && (EMPLOYER as string[]).includes(tab)) {
        setOpenApplicants(null); setBusinessReturn(null)
        setEmployerTab(tab as EmployerTab)
      } else if (side === 'pro' && (PRO as string[]).includes(tab)) {
        if (tab === 'find') setOpenService(null)
        setProTab(tab as ProTab)
      }
    }
    window.addEventListener(GOTO, on)
    return () => window.removeEventListener(GOTO, on)
  }, [side])

  const goToThread = (threadId: string) => {
    setOpenThread(threadId)
    if (side === 'worker') setWorkerTab('messages')
    else setEmployerTab('messages')
  }

  if (memberships === null) return <p className="muted">…</p>

  const hasBusiness = memberships.length > 0
  // Falls back to the first rather than to null: a stale id from a business
  // someone was removed from must not leave the console with nothing to show.
  const employer = memberships.find((m) => m.employerId === activeEmployer) ?? memberships[0]!
  // Somebody who tapped "I'm Hiring" and has not registered yet: show them the
  // form rather than an employer console with nothing in it.
  const showingEmployer = side === 'employer'
  const showingPro = side === 'pro'

  return (
    <div className="w-app">
      {showingPro && (
        <>
          <nav className="w-tabs" data-tabs="5" aria-label={sv('sv.tab.find')}>
            <Tab
              icon="document" label={sv('sv.tab.requests')} current={proTab === 'requests'}
              onClick={() => setProTab('requests')}
            />
            <Tab
              icon="bookmark" label={sv('sv.tab.saved')} current={proTab === 'saved'}
              onClick={() => setProTab('saved')}
            />
            <Tab
              main
              icon="search" label={sv('sv.tab.find')} current={proTab === 'find'}
              onClick={() => { setProTab('find'); setOpenService(null) }}
            />
            <Tab
              icon="wrench" label={sv('sv.tab.ads')} current={proTab === 'ads'}
              badge={newRequests} onClick={() => setProTab('ads')}
            />
            <Tab
              icon="person" label={tw('w.tab.profile')} current={proTab === 'profile'}
              onClick={() => setProTab('profile')}
            />
          </nav>

          {proTab === 'find' && !openService && (
            <ServicesFeed
              signedIn
              onOpen={setOpenService}
              onListYours={() => { setProTab('ads'); setNewService(true) }}
            />
          )}
          {proTab === 'find' && openService && (
            <ServiceDetailView
              key={openService}
              id={openService}
              signedIn
              onBack={() => setOpenService(null)}
              onRequested={() => {}}
            />
          )}
          {proTab === 'saved' && (
            <SavedServices onOpen={(id) => { setOpenService(id); setProTab('find') }} />
          )}
          {proTab === 'requests' && (
            <MyRequests onOpen={(id) => { setOpenService(id); setProTab('find') }} />
          )}
          {proTab === 'profile' && (
            <AccountPanel user={user} onSignedOut={onSignedOut} environment={environment} />
          )}
          {proTab === 'ads' && (
            <MyServices
              startNew={newService}
              onStartedNew={() => setNewService(false)}
              onChanged={refreshRequestCount}
            />
          )}
        </>
      )}

      {showingEmployer && !hasBusiness && (
        <Register
          onRegistered={(m) => {
            setMemberships([m])
            setEmployerTab('postings')
          }}
        />
      )}

      {showingEmployer && hasBusiness && (
        <>
          {/*
            One person can act for more than one business — `employer_members`
            allows it and the plan's "one account, both roles" implies it. A
            console that silently picked the first one would put the second
            restaurant's applicants somewhere with no way to reach them, so the
            picker appears the moment there is a second and never before.
          */}
          {memberships.length > 1 && (
            <label className="w-employer-picker">
              {tw('w.tab.business')}
              <select
                value={employer.employerId}
                onChange={(e) => { setActiveEmployer(e.target.value); setOpenApplicants(null); setBusinessReturn(null) }}
              >
                {memberships.map((m) => (
                  <option key={m.employerId} value={m.employerId}>{m.name}</option>
                ))}
              </select>
            </label>
          )}
          <nav className="w-tabs" data-tabs="5" aria-label={tw('w.tab.business')}>
            <Tab
              icon="home" label={tw('w.tab.home')} current={employerTab === 'home'}
              onClick={() => { setEmployerTab('home'); setOpenApplicants(null) }}
            />
            <Tab
              icon="people" label={tw('w.tab.applicants')} current={employerTab === 'applicants'}
              onClick={() => setEmployerTab('applicants')}
            />
            <Tab
              main
              icon="briefcase" label={tw('w.tab.postings')} current={employerTab === 'postings'}
              onClick={() => { setEmployerTab('postings'); setOpenApplicants(null) }}
            />
            <Tab
              icon="chat" label={tw('w.tab.messages')} current={employerTab === 'messages'}
              badge={unread} onClick={() => setEmployerTab('messages')}
            />
            <Tab
              icon="store" label={tw('w.tab.business')} current={employerTab === 'business'}
              onClick={() => setEmployerTab('business')}
            />
          </nav>

          {employerTab === 'home' && (
            <Dashboard
              membership={employer}
              onPostJob={() => { setEmployerTab('postings'); setNewPost(true) }}
              onApplicants={(jobId) => { setEmployerTab('postings'); setOpenApplicants(jobId) }}
              onGoJobs={() => { setEmployerTab('postings'); setOpenApplicants(null) }}
              onGoMessages={() => setEmployerTab('messages')}
              onReviewBusiness={reviewBusiness}
            />
          )}

          {(employerTab === 'postings' || businessReturn === 'postings') && !openApplicants && (
            <div hidden={employerTab !== 'postings'}><Postings
              key={employer.employerId}
              membership={employer}
              onApplicants={setOpenApplicants}
              startNew={newPost}
              onStartedNew={() => setNewPost(false)}
              onReviewBusiness={reviewBusiness}
            /></div>
          )}
          {/* A posting's applicants, reached from the postings list — the same
              screen the tab shows, opened on one job. */}
          {employerTab === 'postings' && openApplicants && (
            <Applicants
              membership={employer}
              jobId={openApplicants}
              onPickJob={setOpenApplicants}
              onOpenThread={goToThread}
              onBackToPostings={() => setOpenApplicants(null)}
            />
          )}
          {employerTab === 'applicants' && (
            <Applicants
              membership={employer}
              jobId={openApplicants}
              onPickJob={setOpenApplicants}
              onOpenThread={goToThread}
            />
          )}
          {employerTab === 'messages' && <Mailbox openThreadId={openThread} side="employer" />}
          {employerTab === 'business' && (
            <>
            {businessReturn && <BackRow onBack={() => { setEmployerTab(businessReturn); setBusinessReturn(null) }} />}
            <BusinessScreen
              membership={employer}
              user={user}
              onSwitchToWorker={onSwitchToWorker}
              onSignedOut={onSignedOut}
            />
            </>
          )}
        </>
      )}

      {!showingEmployer && !showingPro && (
        <>
          <nav className="w-tabs" data-tabs="5" aria-label={tw('w.tab.jobs')}>
            <Tab
              icon="bookmark" label={tw('w.tab.saved')} current={workerTab === 'saved'}
              onClick={() => setWorkerTab('saved')}
            />
            <Tab
              icon="document" label={tw('w.tab.applications')} current={workerTab === 'applications'}
              onClick={() => setWorkerTab('applications')}
            />
            <Tab
              main
              icon="search" label={tw('w.tab.jobs')} current={workerTab === 'jobs'}
              onClick={() => { setWorkerTab('jobs'); if (!profileReturn) setOpenJob(null) }}
            />
            <Tab
              icon="chat" label={tw('w.tab.messages')} current={workerTab === 'messages'}
              badge={unread} onClick={() => setWorkerTab('messages')}
            />
            <Tab
              icon="person" label={tw('w.tab.profile')} current={workerTab === 'profile'}
              onClick={() => { setProfileVisited(true); setWorkerTab('profile') }}
            />
          </nav>

          {workerTab === 'jobs' && !openJob && (
            <Feed
              onOpen={setOpenJob}
              greetingName={user.displayName}
              onNeedProfile={completeProfile}
            />
          )}
          {openJob && (
            <div hidden={workerTab !== 'jobs'}><Detail
              key={openJob}
              id={openJob}
              signedIn
              onBack={() => { setOpenJob(null); setProfileReturn(null) }}
              onApplied={() => { setProfileReturn(null); setWorkerTab('applications') }}
              onNeedProfile={completeProfile}
              profileRevision={profileRevision}
              active={workerTab === 'jobs'}
            /></div>
          )}
          {workerTab === 'saved' && <Saved onOpen={(id) => { setOpenJob(id); setWorkerTab('jobs') }} />}
          {workerTab === 'applications' && (
            <Applications
              onOpenThread={goToThread}
              onOpenJob={(id) => { setOpenJob(id); setWorkerTab('jobs') }}
            />
          )}
          {workerTab === 'messages' && <Mailbox openThreadId={openThread} side="worker" />}
          {profileVisited && (
            <div hidden={workerTab !== 'profile'}>
              {profileReturn && <div className="w-guidance w-return" role="status">
                <p>{tw(openJob ? 'w.guidance.draftKept' : 'w.guidance.profileNext')}</p>
                <button type="button" onClick={returnFromProfile}>{tw(openJob ? 'w.guidance.backApplication' : 'w.guidance.backJobs')}</button>
              </div>}
              <Profile active={workerTab === 'profile'} focusRequest={profileRequest} returnToApplication={!!profileReturn && !!openJob} onSaved={(p) => {
                if (p.complete) {
                  setProfileRevision((n) => n + 1)
                  if (profileReturnRef.current && workerTabRef.current === 'profile') {
                    setWorkerTab(profileReturnRef.current); setProfileReturn(null)
                  }
                }
              }} />
              {workerTab === 'profile' && <>

              {/*
                Passkeys, devices and notifications live under Profile because
                that is where a person looks for "my account" — core's Account
                panel, unmodified, in this app's information architecture
                rather than on a tab of its own.
              */}
              <AccountPanel user={user} onSignedOut={onSignedOut} environment={environment} />
              </>}
            </div>
          )}
        </>
      )}

    </div>
  )
}
